<template>
  <div
    class="bg-[#F8F7F3] rounded-xl overflow-hidden shadow-sm hover:shadow transition-shadow duration-200 cursor-pointer"
    role="button"
    @click="$emit('watch', video)"
  >
    <div class="relative rounded-[12px] overflow-hidden m-3 sm:m-4 bg-black-08">
      <img
        :src="imageSrc"
        :alt="t('learn.trainingDetail.thumbnailAlt')"
        class="w-full h-[188px] object-contain"
        @error="handleImageError"
      />
      <div
        v-if="!unlocked"
        class="absolute bottom-2 end-2 w-11 h-11 rounded-[8px] bg-[#FFB648] flex items-center justify-center"
      >
        <UIIcon
          name="lock-training"
          class="w-5 h-5 text-black-100"
        />
      </div>
      <div
        v-if="watched"
        class="absolute top-2 start-2 bg-white/90 text-black text-xs px-2 py-0.5 rounded-full"
      >
        {{ t('learn.videoCard.watched') }}
      </div>
    </div>
    <div class="px-3 sm:px-4">
      <h3 class="mt-[18px] font-semibold text-black-100 text-base leading-tight mb-1">
        {{ video.title }}
      </h3>
      <p class="text-sm text-black-70 line-clamp-2 mb-3">
        {{ video.description }}
      </p>
      <div class="flex flex-wrap gap-2 mb-3">
        <span
          v-for="tag in video.tags"
          :key="tag"
          class="text-[12px] leading-none px-[8px] py-[6px] rounded-md bg-[#F3F3F3] text-black-80 border border-black-08"
          >{{ tag }}</span
        >
      </div>
      <div
        v-if="unlocked"
        class="pt-1 pb-4"
      >
        <UICTAButton @click.stop="$emit('watch', video)">{{ t('learn.videoCard.watchDeveloperVault') }}</UICTAButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TrainingVideo } from '~/composables/useTraining';
import UICTAButton from '~/components/ui/UICTAButton.vue';

const { t } = useI18n();

const props = defineProps<{
  video: TrainingVideo;
  unlocked: boolean;
  watched?: boolean;
}>();

defineEmits<{
  (e: 'watch', video: TrainingVideo): void;
}>();

const placeholder = '/images/property.png';

const getImageSrc = (thumbnail: string | undefined) => {
  return thumbnail?.trim() || placeholder;
};

const imageSrc = ref(getImageSrc(props.video.thumbnail));

watch(
  () => props.video.thumbnail,
  (newThumbnail) => {
    imageSrc.value = getImageSrc(newThumbnail);
  },
);

const handleImageError = () => {
  if (imageSrc.value !== placeholder) {
    imageSrc.value = placeholder;
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
